import { useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField } from '@mui/material';

import BoardLayoutTooltip from '../../components/shared/BoardLayoutTooltip';
import CardSortingTooltip from '../../components/shared/CardSortingTooltip';
import CoreQuillEditor from '../../components/shared/CoreQuillEditor';
import {
  DEFAULT_BOARD_COLUMN_COUNT,
  MAX_BOARD_COLUMNS,
  MIN_BOARD_COLUMNS,
  createDefaultBoardColumns,
  resizeBoardColumns,
} from '../../utils/boardColumns';
import type {
  BoardCardSorting,
  BoardColumn,
  BoardLayout,
  BoardVotingStyle,
} from '../../interfaces/BoardDetails';
import useBoardStore from '../../store/boardStore';

interface CreateBoardFormProps {
  appAuthToken: string | null;
  resetVersion: number;
  onBoardCreated: (result: { boardId: string; title: string }) => void;
}

const CreateBoardForm = ({ appAuthToken, resetVersion, onBoardCreated }: CreateBoardFormProps) => {
  const createBoard = useBoardStore((state) => state.createBoard);
  const createError = useBoardStore((state) => state.actionError);
  const clearActionError = useBoardStore((state) => state.clearActionError);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [layout, setLayout] = useState<BoardLayout>('grid');
  const [columns, setColumns] = useState<BoardColumn[]>(
    createDefaultBoardColumns(DEFAULT_BOARD_COLUMN_COUNT)
  );
  const [cardSorting, setCardSorting] = useState<BoardCardSorting>('automatic');
  const [votingStyle, setVotingStyle] = useState<BoardVotingStyle>('dotvoting');
  const [votingEnabled, setVotingEnabled] = useState(true);
  const [privateVotingEnabled, setPrivateVotingEnabled] = useState(false);
  const [participantUploadsEnabled, setParticipantUploadsEnabled] = useState(true);
  const [timerEnabled, setTimerEnabled] = useState(true);
  const [votesPerUser, setVotesPerUser] = useState('3');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    setTitle('');
    setDescription('');
    setLayout('grid');
    setColumns(createDefaultBoardColumns(DEFAULT_BOARD_COLUMN_COUNT));
    setCardSorting('automatic');
    setVotingStyle('dotvoting');
    setVotingEnabled(true);
    setPrivateVotingEnabled(false);
    setParticipantUploadsEnabled(true);
    setTimerEnabled(true);
    setVotesPerUser('3');
    clearActionError();
  }, [clearActionError, resetVersion]);

  const handleCreateBoard = async () => {
    if (!appAuthToken) {
      return;
    }

    setIsCreating(true);
    clearActionError();

    try {
      const trimmedTitle = title.trim();
      const parsedVotesPerUser = Number.parseInt(votesPerUser, 10);
      const nextVotesPerUser =
        votingStyle !== 'dotvoting'
          ? 0
          : Number.isNaN(parsedVotesPerUser)
          ? 3
          : Math.max(0, parsedVotesPerUser);
      const boardId = await createBoard({
        authToken: appAuthToken,
        title: trimmedTitle,
        description: description.trim(),
        source: 'teams',
        layout,
        columns: layout === 'columns' ? columns : [],
        cardSorting,
        votingStyle,
        privateVotingEnabled,
        votingEnabled,
        votesPerUser: nextVotesPerUser,
        participantUploadsEnabled,
        timerEnabled,
      });

      if (!boardId) {
        return;
      }

      onBoardCreated({ boardId, title: trimmedTitle });
    } finally {
      setIsCreating(false);
    }
  };

  const canCreate = title.trim().length > 0 && !isCreating;

  return (
    <Box>
      <Stack spacing={2}>
        <TextField
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          fullWidth
          autoFocus
          disabled={isCreating}
        />
        <Box>
          <Box sx={{ fontSize: 14, mb: 0.75, color: 'text.secondary' }}>Description</Box>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              '.ql-container': { border: 0 },
              '.ql-editor': { minHeight: 120 },
            }}
          >
            <CoreQuillEditor
              value={description}
              onChange={setDescription}
              placeholder="Add a board description..."
            />
          </Box>
        </Box>
        <Box>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
            <Box sx={{ fontSize: 14, color: 'text.secondary' }}>Card sorting</Box>
            <CardSortingTooltip />
          </Stack>
          <TextField
            select
            value={cardSorting}
            onChange={(event) => setCardSorting(event.target.value as BoardCardSorting)}
            fullWidth
            disabled={isCreating}
          >
            <MenuItem value="custom">Custom</MenuItem>
            <MenuItem value="automatic">Automatic</MenuItem>
          </TextField>
        </Box>
        <Box>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
            <Box sx={{ fontSize: 14, color: 'text.secondary' }}>Layout</Box>
            <BoardLayoutTooltip />
          </Stack>
          <TextField
            select
            value={layout}
            onChange={(event) => {
              const nextLayout = event.target.value as BoardLayout;
              setLayout(nextLayout);
              if (nextLayout === 'columns') {
                setColumns((currentColumns) =>
                  resizeBoardColumns(
                    currentColumns,
                    currentColumns.length || DEFAULT_BOARD_COLUMN_COUNT
                  )
                );
              }
            }}
            fullWidth
            disabled={isCreating}
          >
            <MenuItem value="grid">Grid</MenuItem>
            <MenuItem value="lanes">Lanes</MenuItem>
            <MenuItem value="columns">Columns</MenuItem>
          </TextField>
        </Box>
        {layout === 'columns' && (
          <TextField
            type="number"
            label="Number of columns"
            value={columns.length}
            onChange={(event) => {
              const nextCount = Number.parseInt(event.target.value, 10);
              setColumns((currentColumns) => resizeBoardColumns(currentColumns, nextCount));
            }}
            inputProps={{ min: MIN_BOARD_COLUMNS, max: MAX_BOARD_COLUMNS }}
            fullWidth
            disabled={isCreating}
          />
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Timer enabled"
            value={timerEnabled ? 'yes' : 'no'}
            onChange={(event) => setTimerEnabled(event.target.value === 'yes')}
            fullWidth
            disabled={isCreating}
          >
            <MenuItem value="yes">Enabled</MenuItem>
            <MenuItem value="no">Disabled</MenuItem>
          </TextField>
          <TextField
            select
            label="Participant uploads"
            value={participantUploadsEnabled ? 'yes' : 'no'}
            onChange={(event) => setParticipantUploadsEnabled(event.target.value === 'yes')}
            fullWidth
            disabled={isCreating}
          >
            <MenuItem value="yes">Enabled</MenuItem>
            <MenuItem value="no">Disabled</MenuItem>
          </TextField>
          <TextField
            select
            label="Voting style"
            value={votingStyle}
            onChange={(event) => {
              const nextVotingStyle = event.target.value as BoardVotingStyle;
              setVotingStyle(nextVotingStyle);
              if (nextVotingStyle !== 'dotvoting') {
                setPrivateVotingEnabled(false);
                setVotingEnabled(true);
              }
            }}
            fullWidth
            disabled={isCreating}
          >
            <MenuItem value="dotvoting">Dot voting</MenuItem>
            <MenuItem value="liking">Liking</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </TextField>
          {votingStyle === 'dotvoting' && (
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Voting enabled"
                value={votingEnabled ? 'yes' : 'no'}
                onChange={(event) => setVotingEnabled(event.target.value === 'yes')}
                fullWidth
                disabled={isCreating}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
              <TextField
                type="number"
                label="Votes per user"
                value={votesPerUser}
                onChange={(event) => setVotesPerUser(event.target.value)}
                inputProps={{ min: 0 }}
                disabled={!votingEnabled || isCreating}
                fullWidth
              />
              <TextField
                select
                label="Private voting"
                value={privateVotingEnabled ? 'yes' : 'no'}
                onChange={(event) => setPrivateVotingEnabled(event.target.value === 'yes')}
                fullWidth
                disabled={!votingEnabled || isCreating}
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
            </Stack>
          )}
        </Stack>
        <Button
          variant="contained"
          onClick={handleCreateBoard}
          disabled={!canCreate}
          sx={{ alignSelf: 'flex-start' }}
        >
          {isCreating ? 'Creating…' : 'Create board'}
        </Button>
      </Stack>
      {createError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {createError}
        </Alert>
      ) : null}
    </Box>
  );
};

export default CreateBoardForm;
